import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Alert,
    IconButton,
    CircularProgress,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    FormHelperText,
    FormControlLabel,
    Switch,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RequestService from '../../services/RequestService';

// Mantém o estado “limpo” (sem type/actionType no form — serão derivados)
const initialState = {
    title: '',
    message: '',
    target: 'SYSTEM',
    eventId: '',
};

export default function NewNotificationCampaignDialog({
    open,
    onClose,
    onSuccess,
    user,
}) {
    const [formData, setFormData] = useState(initialState);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const [events, setEvents] = useState([]);
    const [loadingEvents, setLoadingEvents] = useState(false);

    useEffect(() => {
        if (open && formData.target === 'EVENT') {
            loadEvents();
        }
        // eslint-disable-next-line
    }, [open, formData.target]);

    const loadEvents = async () => {
        try {
            setLoadingEvents(true);

            // Ajuste aqui conforme seu endpoint admin de events
            // Seu RequestService.getEvents hoje chama /events?page&size
            // Se você tiver endpoint admin, troque pra ele.
            const data = await RequestService.getEvents({ page: 0, size: 100 });

            setEvents(data.content || data.events || data || []);
        } catch (err) {
            console.error('Erro ao carregar eventos:', err);
            setEvents([]);
        } finally {
            setLoadingEvents(false);
        }
    };

    const resetForm = () => {
        setFormData(initialState);
        setError(null);
        setSubmitting(false);
        setEvents([]);
        setLoadingEvents(false);
    };

    const handleClose = (event, reason) => {
        if (reason === 'backdropClick') return;
        resetForm();
        onClose?.(event, reason);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleTargetChange = (e, newTarget) => {
        // ToggleButtonGroup pode mandar null se clicar no mesmo botão (depende do config)
        if (!newTarget) return;

        setFormData((prev) => ({
            ...prev,
            target: newTarget,
            eventId: newTarget === 'EVENT' ? prev.eventId : '',
        }));
    };

    // Derivados: type/actionType/actionId (travados)
    const derived = useMemo(() => {
        const hasEvent =
            formData.target === 'EVENT' &&
            !!formData.eventId &&
            String(formData.eventId).trim() !== '';

        return {
            type: hasEvent ? 'EVENT' : 'SYSTEM',
            actionType: hasEvent ? 'EVENT' : 'NONE',
            actionId: hasEvent ? parseInt(formData.eventId, 10) : null,
            actionUrl: null,
        };
    }, [formData.target, formData.eventId]);

    const validate = () => {
        if (!formData.title.trim()) return 'Informe um título para a mensagem.';
        if (formData.title.length > 150)
            return 'O título deve ter no máximo 150 caracteres.';

        if (!formData.message.trim()) return 'Informe o conteúdo da mensagem.';
        if (formData.message.length > 255)
            return 'A mensagem deve ter no máximo 255 caracteres.';

        if (formData.target === 'EVENT') {
            if (!formData.eventId) return 'Selecione um evento.';
        }

        return null;
    };

    const handleSubmit = async () => {
        setError(null);

        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }

        try {
            setSubmitting(true);

            const payload = {
                title: formData.title.trim(),
                message: formData.message.trim(),

                type: derived.type,
                actionType: derived.actionType,
                actionId: derived.actionId,
                actionUrl: derived.actionUrl,

                eventId:
                    formData.eventId && formData.eventId !== ''
                        ? parseInt(formData.eventId, 10)
                        : null,
            };

            await RequestService.createNotificationCampaign(payload);

            onSuccess?.(
                'Campanha criada com sucesso! Use "Enviar" para disparar.'
            );
            resetForm();
            onClose?.();
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                err?.response?.data ||
                'Erro ao criar a campanha de notificação.';
            setError(
                typeof message === 'string'
                    ? message
                    : 'Erro ao criar a campanha.'
            );
        } finally {
            setSubmitting(false);
        }
    };

    const isEventFlow = formData.target === 'EVENT';

    return (
        <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
            <DialogTitle>
                Nova Mensagem
                <IconButton
                    aria-label='close'
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        mt: 1,
                    }}
                >
                    <TextField
                        label='Título'
                        name='title'
                        value={formData.title}
                        onChange={handleChange}
                        fullWidth
                        disabled={submitting}
                        inputProps={{ maxLength: 150 }}
                        helperText={`${formData.title.length}/150 caracteres`}
                    />

                    <TextField
                        label='Mensagem'
                        name='message'
                        value={formData.message}
                        onChange={handleChange}
                        fullWidth
                        multiline
                        rows={4}
                        disabled={submitting}
                        inputProps={{ maxLength: 255 }}
                        helperText={`${formData.message.length}/255 caracteres`}
                    />

                    <Box>
                        <InputLabel sx={{ mb: 1 }}>Tipo da mensagem</InputLabel>

                        <ToggleButtonGroup
                            value={formData.target}
                            exclusive
                            onChange={handleTargetChange}
                            disabled={submitting}
                            fullWidth
                            sx={{ display: 'flex' }}
                        >
                            <ToggleButton value='SYSTEM' sx={{ flex: 1 }}>
                                Geral
                            </ToggleButton>
                            <ToggleButton value='EVENT' sx={{ flex: 1 }}>
                                Evento
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    {/* Select de evento só aparece se sim */}
                    {isEventFlow && (
                        <FormControl
                            fullWidth
                            disabled={submitting || loadingEvents}
                        >
                            <InputLabel>Evento</InputLabel>
                            <Select
                                name='eventId'
                                value={formData.eventId}
                                onChange={handleChange}
                                label='Evento'
                            >
                                <MenuItem value=''>
                                    <em>Selecione</em>
                                </MenuItem>

                                {events.map((ev) => (
                                    <MenuItem key={ev.id} value={ev.id}>
                                        {ev.title}
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>
                                {loadingEvents
                                    ? 'Carregando eventos...'
                                    : 'Selecione qual evento será aberto ao clicar na notificação'}
                            </FormHelperText>
                        </FormControl>
                    )}

                    {error && <Alert severity='error'>{error}</Alert>}
                </Box>
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={handleClose}
                    variant='outlined'
                    disabled={submitting}
                >
                    Cancelar
                </Button>
                <Button
                    onClick={handleSubmit}
                    color='primary'
                    variant='contained'
                    disabled={submitting}
                    endIcon={submitting ? <CircularProgress size={18} /> : null}
                >
                    {submitting ? 'Criando...' : 'Criar Campanha'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
